/**
 * Debug overlay (009). Renders live diagnostics on the device (works on mobile,
 * where browser devtools don't) and the export/record controls. Only mounted by
 * DebugRoot when debug is enabled, so it can assume it should run.
 */
import { useEffect, useState } from 'react'
import { snapshot, type DiagnosticSnapshot, type StreamStat } from './collector.js'
import { collectDeviceInfo } from './deviceInfo.js'
import { copyReport, downloadReport } from './report.js'
import { startRecording, stopRecording, isRecording } from './recorder.js'
import styles from './DebugPanel.module.css'

const POLL_MS = 2000

function streamLine(s: StreamStat): string {
  const dir = s.direction === 'send' ? '▲' : '▼'
  const res = s.width && s.height ? `${s.width}×${s.height}` : '—'
  const fps = s.fps !== undefined ? `@${s.fps}` : ''
  const impl = s.implementation ? ` ${s.implementation}` : ''
  const br = s.bitrateKbps !== undefined ? ` ${s.bitrateKbps}kbps` : ''
  const loss = s.lossPct !== undefined ? ` loss ${s.lossPct}%` : s.packetsLost !== undefined ? ` lost ${s.packetsLost}` : ''
  const rtt = s.rttMs !== undefined ? ` rtt ${s.rttMs}ms` : ''
  const qlr = s.qualityLimitationReason && s.qualityLimitationReason !== 'none' ? ` ⚠${s.qualityLimitationReason}` : ''
  const frz = s.freezeCount ? ` frz ${s.freezeCount}` : ''
  const svc = s.scalabilityMode ? ` ${s.scalabilityMode}` : ''
  return `${dir} ${s.kind} ${s.codec ?? '—'}${impl}${svc} ${res}${fps}${br}${loss}${rtt}${frz}${qlr}`
}

export function DebugPanel() {
  const [snap, setSnap] = useState<DiagnosticSnapshot | null>(null)
  const [recording, setRecording] = useState(isRecording())
  const [collapsed, setCollapsed] = useState(false)
  const [flash, setFlash] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    const tick = () => {
      void snapshot().then((s) => {
        if (active) setSnap(s)
      })
    }
    tick()
    const id = setInterval(tick, POLL_MS)
    return () => {
      active = false
      clearInterval(id)
    }
  }, [])

  const device = collectDeviceInfo()

  const showFlash = (msg: string) => {
    setFlash(msg)
    setTimeout(() => setFlash(null), 1500)
  }

  const onCopy = () => {
    void copyReport().then((ok) => showFlash(ok ? 'Report copied' : 'Copy failed'))
  }
  const onDownload = () => {
    void downloadReport().then((ok) => showFlash(ok ? 'Report saved' : 'Save failed'))
  }
  const onToggleRecord = () => {
    if (isRecording()) {
      stopRecording()
      setRecording(false)
      showFlash('Recording stopped — include via report')
    } else {
      startRecording()
      setRecording(true)
      showFlash('Recording…')
    }
  }

  return (
    <div data-testid="debug-panel" className={styles.panel} role="region" aria-label="Debug diagnostics">
      <div className={styles.header}>
        <span className={styles.title}>debug</span>
        <span className={styles.status} data-testid="debug-status">
          {snap?.connectionStatus ?? '—'}
        </span>
        <button type="button" className={styles.btn} onClick={() => setCollapsed((c) => !c)}>
          {collapsed ? '▢' : '▁'}
        </button>
      </div>

      {!collapsed && (
        <div className={styles.body}>
          <div className={styles.device}>
            {device.isMobile ? 'mobile' : 'desktop'} · cap {device.captureProfile.width ?? '?'}×
            {device.captureProfile.height ?? '?'}@{device.captureProfile.fps ?? '?'} ·{' '}
            {device.captureProfile.maxBitrateKbps}kbps
          </div>

          <div className={styles.streams} data-testid="debug-streams">
            {(snap?.streams ?? []).length === 0 && <div className={styles.muted}>no media yet</div>}
            {(snap?.streams ?? []).map((s, i) => (
              <div
                key={`${s.direction}-${s.ssrc ?? i}`}
                className={s.direction === 'send' ? styles.send : styles.recv}
              >
                {streamLine(s)}
              </div>
            ))}
          </div>

          <div className={styles.quality}>
            quality: you={snap?.quality.local ?? '—'}
            {Object.entries(snap?.quality.peers ?? {}).map(([id, q]) => (
              <span key={id}> · {id.slice(0, 4)}={q}</span>
            ))}
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.btn} data-testid="debug-copy" onClick={onCopy}>
              Copy report
            </button>
            <button type="button" className={styles.btn} data-testid="debug-download" onClick={onDownload}>
              Download
            </button>
            <button
              type="button"
              className={`${styles.btn} ${recording ? styles.recording : ''}`}
              data-testid="debug-record"
              onClick={onToggleRecord}
            >
              {recording ? '■ Stop rec' : '● Record'}
            </button>
          </div>

          {flash && <div className={styles.flash}>{flash}</div>}
        </div>
      )}
    </div>
  )
}
